import { colors } from '@utility/sales-forecast';
import { theme } from '@utility/theme';
import { Line } from 'recharts';

/**
 * Default options for the lines in SeasonalityChartLines component.
 * @type {Object}
 * @property {boolean} dot - Specifies whether to show dots on the lines (default: false).
 * @property {number} strokeWidth - The width of the line stroke (default: 2).
 *
 * @see {@link https://recharts.org/en-US/api/Line | Line Documentation}
 */
const defaultOptions = {
  dot: false,
  strokeWidth: 2,
};

/**
 * SeasonalityChartLines component displaying lines for different regions' data.
 * @param {string[]} props.excludedRegions - Array of regions to be excluded.
 * @param {string[]} props.marketplaceRegionIds - Array of marketplace region IDs.
 * @param {Object} props.marketplaceRegionNames - Object containing marketplace region names indexed by IDs.
 * @returns {JSX.Element} - React component for displaying lines for different regions' data.
 */
function SeasonalityChartLines(props) {
  const {
    excludedRegions = [],
    marketplaceRegionIds = [],
    marketplaceRegionNames = {},
  } = props;

  /**
   * Checks if a value is included in the excludedRegions array.
   * @param {string} [value=''] - The value to be checked against excludedRegions.
   * @returns {boolean} - Returns true if the value is found in excludedRegions, otherwise false.
   */
  const checkIsExcluded = (value = '') => excludedRegions.includes(value);

  return (
    <>
      {/* Line for global data */}
      <Line
        {...defaultOptions}
        animationDuration={150}
        dataKey="global"
        hide={checkIsExcluded('global')}
        name="Predicted"
        stroke={colors.lineGlobal}
      />

      {/* Lines for marketplace region data */}
      {marketplaceRegionIds.map((id, i) => (
        <Line
          {...defaultOptions}
          animationDuration={150}
          dataKey={id}
          hide={checkIsExcluded(marketplaceRegionNames?.[id])}
          key={id}
          name={marketplaceRegionNames?.[id] ?? id}
          stroke={colors.lineColors[i]}
        />
      ))}

      {/* Line for past data */}
      <Line
        {...defaultOptions}
        animationDuration={150}
        dataKey="past"
        name="Past"
        stroke={theme.colors.grey}
        strokeDasharray="5 5"
      />
    </>
  );
}

export default SeasonalityChartLines;
